import React from "react";
import "../../../../core/common/customPagination/customPagination.css";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  total,
  limit,
  hasNextPage,
  hasPrevPage,
  onPageChange,
  onLimitChange,
}) => {
  const handlePrevPage = () => {
    if (hasPrevPage) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirstPage = () => {
    if (currentPage > 1) {
      onPageChange(1);
    }
  };

  const handleLastPage = () => {
    if (currentPage < totalPages) {
      onPageChange(totalPages);
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value);
    if (onLimitChange) {
      onLimitChange(newLimit);
    }
  };

  const startRecord = (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, total);

  
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 3; 
    const currentPageNum = currentPage;
    const totalPagesNum = totalPages;

    if (totalPagesNum <= maxVisible) {
      
      for (let i = 1; i <= totalPagesNum; i++) {
        pages.push(i);
      }
    } else {
      
      if (currentPageNum === 1) {
        
        pages.push(1, 2, "...", totalPagesNum);
      } else if (currentPageNum === totalPagesNum) {
        
        pages.push(1, "...", totalPagesNum - 1, totalPagesNum);
      } else {
        
        pages.push(1, "...", currentPageNum, "...", totalPagesNum);
      }
    }

    return pages;
  };

  if (totalPages < 1) return null;

  return (
    <div className="custom-pagination">
      {onLimitChange && (
        <div className="custom-pagination__limit">
          <span>Show</span>
          <select
            value={limit}
            onChange={handleLimitChange}
            className="custom-pagination__select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>30</option>
            <option value={100}>50</option>
          </select>
          <span style={{ whiteSpace: "nowrap" }}>per page</span>
        </div>
      )}

      <div className="custom-pagination__info">
        Showing {startRecord} to {endRecord} of {total} records
      </div>

      <div className="custom-pagination__controls">
        <button
          onClick={handleFirstPage}
          disabled={!hasPrevPage}
          className="custom-pagination__btn"
        >
          <ChevronsLeft size={16} />
        </button>

        <button
          onClick={handlePrevPage}
          disabled={!hasPrevPage}
          className="custom-pagination__btn"
        >
          <ChevronLeft size={16} />
        </button>

        {getVisiblePages().map((pageNum, index) =>
          typeof pageNum === "string" ? (
            <span
              key={`ellipsis-${index}`}
              className="custom-pagination__ellipsis"
            >
              {pageNum}
            </span>
          ) : (
            <button
              key={pageNum}
              onClick={() => pageNum !== currentPage && onPageChange(pageNum)}
              disabled={pageNum === currentPage}
              className={`custom-pagination__btn ${currentPage === pageNum ? "custom-pagination__btn--active" : ""
                }`}
            >
              {pageNum}
            </button>
          ),
        )}

        <button
          onClick={handleNextPage}
          disabled={!hasNextPage}
          className="custom-pagination__btn"
        >
          <ChevronRight size={16} />
        </button>

        <button
          onClick={handleLastPage}
          disabled={!hasNextPage}
          className="custom-pagination__btn"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default CustomPagination;
